import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import PageWrapper from "../modules/PageWrapper";
import { useGetMemberEminuteByIdQuery } from "../../../store/member/eminutesSlice";
import { dateFromString } from "../../../utils";
import { Loading } from "../../../components";
import { BsFileEarmarkText, BsArrowLeft } from "react-icons/bs";
import moment from "moment";

export default function MemberEminuteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data = {}, isLoading, error, isError } = useGetMemberEminuteByIdQuery(id, {
    skip: !id,
  });

  const dateStr = data.created_at ? dateFromString(data.created_at) : "—";
  const dateDisplay = data.created_at ? moment(data.created_at).format("DD MMMM YYYY") : "—";

  if (isLoading) {
    return (
      <PageWrapper title="E-Minute" back>
        <DetailWrapper>
          <div className="loading-state">
            <Loading />
          </div>
        </DetailWrapper>
      </PageWrapper>
    );
  }

  if (isError || error || (!data?.id && !isLoading)) {
    return (
      <PageWrapper title="E-Minute" back>
        <DetailWrapper>
          <div className="error-state">
            <div className="error-icon">!</div>
            <h4 className="error-title">E-Minute not found</h4>
            <p className="error-message">
              This E-Minute is not available or could not be found.
            </p>
            <button
              type="button"
              className="back-btn"
              onClick={() => navigate("/member/eminutes")}
            >
              <BsArrowLeft size={18} className="me-2" />
              Back to E-Minutes
            </button>
          </div>
        </DetailWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="E-Minute" back>
      <DetailWrapper>
        <article className="eminute-article">
          <header className="article-header">
            <div className="header-badge">
              <BsFileEarmarkText size={18} />
              <span>E-Minute</span>
            </div>
            <h1 className="article-title">{data.title || "Untitled"}</h1>
            <time className="article-date" dateTime={data.created_at}>
              {dateDisplay}
            </time>
          </header>

          <div
            className="article-content"
            dangerouslySetInnerHTML={{
              __html: data.content || '<p class="text-muted">No content available.</p>',
            }}
          />
        </article>
      </DetailWrapper>
    </PageWrapper>
  );
}

const DetailWrapper = styled.div`
  .loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 280px;
  }

  .error-state {
    text-align: center;
    padding: 3rem 2rem;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e5e5;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .error-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 67, 67, 0.1);
    color: #ff4343;
    font-size: 24px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
  }

  .error-title {
    font-size: 18px;
    font-weight: 600;
    color: #45464e;
    margin-bottom: 0.5rem;
  }

  .error-message {
    font-size: 14px;
    color: #525252;
    margin-bottom: 1.5rem;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.625rem 1.25rem;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-primary);
    background: transparent;
    border: 2px solid var(--color-primary);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(139, 49, 58, 0.08);
    }
  }

  .eminute-article {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e5e5;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .article-header {
    padding: 2rem 2rem 1.5rem;
    border-bottom: 1px solid #e5e5e5;
    background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  }

  .header-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: linear-gradient(135deg, rgba(139, 49, 58, 0.08) 0%, rgba(49, 119, 140, 0.08) 100%);
    color: var(--color-primary);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .article-title {
    font-size: 24px;
    font-weight: 600;
    color: #45464e;
    line-height: 1.35;
    margin: 0 0 0.75rem 0;
  }

  .article-date {
    font-size: 14px;
    color: #525252;
    font-weight: 400;
  }

  .article-content {
    padding: 2rem;
    font-size: 15px;
    line-height: 1.7;
    color: #45464e;

    /* Typography for HTML content */
    p {
      margin-bottom: 1rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    h1, h2, h3, h4 {
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
      font-weight: 600;
      color: #383838;
    }

    h1 { font-size: 1.5rem; }
    h2 { font-size: 1.25rem; }
    h3 { font-size: 1.125rem; }
    h4 { font-size: 1rem; }

    ul, ol {
      margin: 1rem 0;
      padding-left: 1.5rem;
    }

    li {
      margin-bottom: 0.5rem;
    }

    a {
      color: var(--color-secondary);
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
      font-size: 14px;
    }

    th, td {
      padding: 0.75rem 1rem;
      border: 1px solid #e0e0e0;
      text-align: left;
    }

    th {
      background: #f7f7f7;
      font-weight: 600;
      color: #383838;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }

    blockquote {
      margin: 1rem 0;
      padding: 1rem 1.25rem;
      border-left: 4px solid var(--color-primary);
      background: #fafafa;
      color: #525252;
      font-style: italic;
    }
  }
`;
